import type { Placement } from '@popperjs/core'
import React, { type ReactNode, useState } from 'react'
import { usePopper } from 'react-popper'

import { FocusOn } from '../../components/FocusOn'
import { CssVars } from '../themes'
import { Layer, PersistedLayer } from './Layer'

type Props = {
  children:
    | ReactNode
    | ((_: { actualPlacement: Placement | undefined }) => ReactNode)
  /**
   * Spacing between the AnchoredOverlay and the target element in pixels.
   */
  gap?: number
  /**
   * Offset of AnchoredOverlay, this will offset in different directions depending on the placement.
   */
  skidding?: number
  placement?: Placement
  /**
   * Controls whether AnchoredOverlay is open or not.
   */
  isOpen: boolean
  isPersistent?: boolean
  /**
   * Callback to close the AnchoredOverlay.
   */
  onClose?: () => void
  /**
   * Controls whether focus should be locked within.
   */
  shouldLockFocus?: boolean
  /**
   * HTML element that will AnchoredOverlay will be attached to.
   */
  targetEl: HTMLElement | null
  /**
   * Z-index of the overlay. Use sparingly as this may be deprecated in the future.
   */
  dangerouslyEnforceZIndex?: string | null
}

/**
 * An anchored overlay that automatically positions itself around a target element
 * based on `placement` props
 */
export function AnchoredOverlay({
  children,
  dangerouslyEnforceZIndex,
  gap = 0,
  isOpen,
  isPersistent = false,
  onClose,
  placement = 'bottom',
  shouldLockFocus = false,
  skidding = 0,
  targetEl,
}: Props) {
  const [anchoredOverlayEl, setAnchoredOverlayEl] =
    useState<HTMLDivElement | null>(null)

  const {
    attributes: popperAttributes,
    forceUpdate: popperForceUpdate,
    state: popperState,
    styles: popperStyles,
  } = usePopper(targetEl, anchoredOverlayEl, {
    modifiers: [
      { name: 'offset', options: { offset: [skidding, gap] } },
      { name: 'flip', options: { padding: 8 } },
    ],
    placement,
  })

  React.useLayoutEffect(() => {
    // NOTE(yan): force a recalculation immediately before the contents of a
    //   persistent anchored overlay are visible to ensure they're positioned correctly.
    //   this is because the contents of a persistent anchored overlay are always in the
    //   dom, but hidden with display:none. however, popper still tries to
    //   position them correctly, but since hidden elements has an empty
    //   bounding rect, the position will be incorrect until the next popper
    //   positioning update that happens on a viewport change.
    if (isPersistent && isOpen && popperForceUpdate) {
      popperForceUpdate()
    }
  }, [isOpen, isPersistent, popperForceUpdate])

  const handleDocumentClick = React.useCallback(
    (e: MouseEvent) => {
      const target = e.target
      if (
        isOpen &&
        // NOTE(yan): use Element instead of HTMLElement because click might originate
        //   from an element within a SVG (ex. icon) which isn't an HTMLElement.
        target instanceof Element &&
        !targetEl?.contains(target) &&
        !anchoredOverlayEl?.contains(target)
      ) {
        onClose?.()
      }
    },
    [isOpen, onClose, anchoredOverlayEl, targetEl]
  )

  const handleEscape = React.useCallback(() => {
    if (isOpen) {
      onClose?.()
    }
  }, [isOpen, onClose])

  const zIndex =
    dangerouslyEnforceZIndex ?? CssVars.size._experimentalPopoverZIndex
  const content = (
    <div
      ref={setAnchoredOverlayEl}
      style={popperStyles.popper}
      {...popperAttributes.popper}
    >
      {/* NOTE(omer.demirkan) If you touch this code later,
      please consider adding isolation with aria-hidden
      and adding scroll locking. */}
      <FocusOn
        autoFocus
        enabled={shouldLockFocus}
        noIsolation
        returnFocus
        scrollLock={false}
      >
        {typeof children === 'function'
          ? children({ actualPlacement: popperState?.placement })
          : children}
      </FocusOn>
    </div>
  )

  if (isPersistent) {
    return (
      <PersistedLayer
        DANGEROUSLY_SET_Z_INDEX={zIndex}
        isActive={isOpen}
        onDocumentClick={handleDocumentClick}
        onEscape={handleEscape}
      >
        {content}
      </PersistedLayer>
    )
  }

  return isOpen ? (
    <Layer
      DANGEROUSLY_SET_Z_INDEX={zIndex}
      onDocumentClick={handleDocumentClick}
      onEscape={handleEscape}
    >
      {content}
    </Layer>
  ) : null
}
